/**
 * Cookie Consent Utility
 *
 * Manages user consent for analytics tracking per GDPR/CCPA requirements.
 * Consent is stored in localStorage and checked before initializing analytics.
 */

const CONSENT_STORAGE_KEY = "koru_cookie_consent";

export type ConsentStatus = "pending" | "accepted" | "rejected";

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
}

export interface ConsentState {
  status: ConsentStatus;
  preferences: ConsentPreferences | null;
}

/**
 * Get the current consent state from localStorage
 */
export const getConsentState = (): ConsentState => {
  if (typeof window === "undefined") {
    return { status: "pending", preferences: null };
  }

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) {
      return { status: "pending", preferences: null };
    }

    const parsed = JSON.parse(stored) as ConsentPreferences;
    // If we have stored preferences with analytics consent, consider it accepted
    if (parsed?.timestamp) {
      return {
        status: parsed.analytics ? "accepted" : "rejected",
        preferences: parsed,
      };
    }

    return { status: "pending", preferences: null };
  } catch (error) {
    console.warn("Error reading consent state:", error);
    return { status: "pending", preferences: null };
  }
};

/**
 * Check if user has given consent for analytics
 */
export const hasAnalyticsConsent = (): boolean => {
  const state = getConsentState();
  return state.status === "accepted" && state.preferences?.analytics === true;
};

/**
 * Check if consent decision has been made (either accepted or rejected)
 */
export const hasConsentDecision = (): boolean => {
  const state = getConsentState();
  return state.status !== "pending";
};

/**
 * Save consent preferences to localStorage
 */
export const saveConsentPreferences = (
  preferences: Omit<ConsentPreferences, "timestamp">
): void => {
  if (typeof window === "undefined") return;

  try {
    const consentData: ConsentPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
  } catch (error) {
    console.error("Error saving consent preferences:", error);
  }
};

/**
 * Accept all cookies - enables all tracking
 */
export const acceptAllCookies = (): void => {
  saveConsentPreferences({
    analytics: true,
    marketing: true,
  });
};

/**
 * Accept only essential cookies - disables analytics
 */
export const acceptEssentialOnly = (): void => {
  saveConsentPreferences({
    analytics: false,
    marketing: false,
  });
};

/**
 * Clear consent preferences (for testing or user request)
 */
export const clearConsentPreferences = (): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing consent preferences:", error);
  }
};

/**
 * Track consent event (call this after analytics is initialized)
 */
export const trackConsentEvent = (
  decision: "accepted" | "rejected" | "customized",
  preferences?: Partial<ConsentPreferences>
): void => {
  // This will be called after analytics init, so gtag should be available
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "consent_update", {
      event_category: "consent",
      event_label: decision,
      analytics_consent: preferences?.analytics ?? decision === "accepted",
      marketing_consent: preferences?.marketing ?? decision === "accepted",
    });
  }
};

// Type augmentation for window.gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
  }
}
